import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { X, RefreshCw, Sparkles, Wand2, Send, RotateCcw, ChevronDown, Brain, Languages } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import GlobalContextBar from '../components/GlobalContextBar';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import TranslationService from '../services/translationService';
import ApiService from '../services/apiService';
import { readSSE } from '../utils/sseStream';

const API_URL = ApiService.BASE_URL;

// Spelling-insensitive key, same as the backend's ingredient_key: 'Metalaxyl M' == 'metalaxyl-m';
// `aliases` (from /v1/pesticide-materials) maps misspellings and synonyms: 'mefenoxam' -> 'metalaxyl-m'
const materialKey = (s, aliases = {}) => {
  const key = String(s || '').toLowerCase().replace(/\([^)]*\)/g, ' ').replace(/[\s-]+/g, '-').replace(/^-+|-+$/g, '');
  return aliases[key] || key;
};
const typeKey = (s) => String(s || '').trim().replace(/ي/g, 'ى').replace(/\s+/g, ' ');

// Items of the AI's "MATERIALS: [a, b, ...]" line. Some ingredient names contain commas
// ("1,3-Dichloropropene"), so neighbouring pieces are re-joined when that forms a known name.
function parseSuggestedMaterials(listText, materialsList, aliases) {
  const byKey = new Map(materialsList.map(m => [materialKey(m, aliases), m]));
  const pieces = listText.split(',').map(s => s.trim().replace(/^['"]|['"]$/g, ''));
  const found = [];
  let i = 0;
  while (i < pieces.length) {
    let matched = false;
    for (let span = Math.min(4, pieces.length - i); span >= 1; span--) {
      const name = byKey.get(materialKey(pieces.slice(i, i + span).join(','), aliases));
      if (name) {
        if (!found.includes(name)) found.push(name);
        i += span;
        matched = true;
        break;
      }
    }
    if (!matched) {
      // A mixture written out ("Mancozeb + Cymoxanil") adds its single ingredients
      pieces[i].split(/\s*[+&]\s*|\s+-\s*|\s*-\s+/).forEach(part => {
        const name = byKey.get(materialKey(part, aliases));
        if (name && !found.includes(name)) found.push(name);
      });
      i += 1;
    }
  }
  return found;
}

// ─── Pesticide Type Cards (multi-select, expressive icons) ──────────────────
const PESTICIDE_TYPES = [
  {
    id: 'حشرى',
    icon: (
      <svg viewBox="0 0 64 64" className="w-9 h-9" fill="none">
        {/* Expressive beetle / insect icon */}
        <ellipse cx="32" cy="36" rx="12" ry="16" fill="currentColor" opacity="0.9"/>
        <ellipse cx="32" cy="24" rx="8" ry="7" fill="currentColor"/>
        {/* Antennae */}
        <line x1="28" y1="18" x2="18" y2="8"  stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="36" y1="18" x2="46" y2="8"  stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
        <circle cx="18" cy="8"  r="2.5" fill="currentColor"/>
        <circle cx="46" cy="8"  r="2.5" fill="currentColor"/>
        {/* Wing lines */}
        <line x1="22" y1="30" x2="10" y2="24" stroke="white" strokeWidth="1.5" strokeLinecap="round" opacity="0.5"/>
        <line x1="42" y1="30" x2="54" y2="24" stroke="white" strokeWidth="1.5" strokeLinecap="round" opacity="0.5"/>
        {/* Legs */}
        <line x1="21" y1="36" x2="8"  y2="32" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        <line x1="21" y1="42" x2="8"  y2="42" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        <line x1="43" y1="36" x2="56" y2="32" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        <line x1="43" y1="42" x2="56" y2="42" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        {/* Eyes */}
        <circle cx="28" cy="23" r="2" fill="white"/>
        <circle cx="36" cy="23" r="2" fill="white"/>
        <circle cx="28.7" cy="23.5" r="1" fill="#1a1a1a"/>
        <circle cx="36.7" cy="23.5" r="1" fill="#1a1a1a"/>
      </svg>
    ),
    labelAr: 'حشرى',       labelEn: 'Insecticide',
    descAr: 'مكافحة الحشرات', descEn: 'Controls insects',
    glowColor:   'rgba(249,115,22,0.5)',
    colorClass:  'text-orange-500',
    bg:          'bg-orange-50 dark:bg-orange-900/20',
    border:      'border-orange-300 dark:border-orange-500',
    selectedBg:  'from-orange-400 to-red-500',
    selectedRing:'ring-orange-400',
  },
  {
    id: 'فطرى',
    icon: (
      <svg viewBox="0 0 64 64" className="w-9 h-9" fill="none">
        {/* Mushroom / fungus icon */}
        <ellipse cx="32" cy="28" rx="20" ry="14" fill="currentColor" opacity="0.9"/>
        {/* Spots on cap */}
        <circle cx="24" cy="24" r="3" fill="white" opacity="0.5"/>
        <circle cx="36" cy="20" r="2.5" fill="white" opacity="0.5"/>
        <circle cx="42" cy="28" r="2" fill="white" opacity="0.4"/>
        <circle cx="22" cy="30" r="1.5" fill="white" opacity="0.3"/>
        {/* Stem */}
        <rect x="26" y="40" width="12" height="14" rx="6" fill="currentColor" opacity="0.7"/>
        {/* Gills under cap */}
        <path d="M14 38 Q20 42 32 42 Q44 42 50 38" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.4"/>
        {/* Spores floating */}
        <circle cx="12" cy="18" r="2" fill="currentColor" opacity="0.4"/>
        <circle cx="52" cy="22" r="1.5" fill="currentColor" opacity="0.3"/>
        <circle cx="8"  cy="28" r="1.5" fill="currentColor" opacity="0.25"/>
      </svg>
    ),
    labelAr: 'فطرى',          labelEn: 'Fungicide',
    descAr: 'مكافحة الفطريات', descEn: 'Controls fungi',
    glowColor:   'rgba(168,85,247,0.5)',
    colorClass:  'text-purple-500',
    bg:          'bg-purple-50 dark:bg-purple-900/20',
    border:      'border-purple-300 dark:border-purple-500',
    selectedBg:  'from-purple-400 to-fuchsia-600',
    selectedRing:'ring-purple-400',
  },
  {
    id: 'حشائش',
    icon: (
      <svg viewBox="0 0 64 64" className="w-9 h-9" fill="none">
        {/* Weed / grass with X = herbicide */}
        {/* Grass blades */}
        <path d="M20 52 Q16 36 22 24 Q26 14 24 8"  stroke="currentColor" strokeWidth="3" strokeLinecap="round" fill="none" opacity="0.8"/>
        <path d="M32 52 Q30 34 32 20 Q33 10 32 4"  stroke="currentColor" strokeWidth="3" strokeLinecap="round" fill="none"/>
        <path d="M44 52 Q48 36 42 24 Q38 14 40 8"  stroke="currentColor" strokeWidth="3" strokeLinecap="round" fill="none" opacity="0.8"/>
        {/* Side shoots */}
        <path d="M24 30 Q18 26 14 28" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" fill="none" opacity="0.6"/>
        <path d="M40 30 Q46 26 50 28" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" fill="none" opacity="0.6"/>
        {/* Ground */}
        <line x1="8" y1="52" x2="56" y2="52" stroke="currentColor" strokeWidth="3" strokeLinecap="round" opacity="0.5"/>
        {/* Red X overlay = herbicide kill */}
        <line x1="10" y1="10" x2="22" y2="22" stroke="#ef4444" strokeWidth="3.5" strokeLinecap="round"/>
        <line x1="22" y1="10" x2="10" y2="22" stroke="#ef4444" strokeWidth="3.5" strokeLinecap="round"/>
      </svg>
    ),
    labelAr: 'مبيد حشائش', labelEn: 'Herbicide',
    descAr: 'مكافحة الأعشاب', descEn: 'Controls weeds',
    glowColor:   'rgba(34,197,94,0.5)',
    colorClass:  'text-green-500',
    bg:          'bg-green-50 dark:bg-green-900/20',
    border:      'border-green-300 dark:border-green-500',
    selectedBg:  'from-green-400 to-emerald-600',
    selectedRing:'ring-green-400',
  },
];

// ─── Formulation Groups ──────────────────────────────────────────────────────
const FORMULATION_GROUPS = [
  { id: 'liquid',   emoji: '💧', labelAr: 'سائل',          labelEn: 'Liquid',    values: ['EC','SC','SL','EW','OD','AS','SE','ME','DC','FL','FS','SS','WSC','سائل (L)'],   selectedClass: 'bg-blue-100 border-blue-400 dark:bg-blue-900/40 dark:border-blue-400 text-blue-700 dark:text-blue-300' },
  { id: 'powder',   emoji: '🌫️', labelAr: 'بودرة / مسحوق', labelEn: 'Powder',   values: ['SP','WP','DP','WS','مسحوق تعفير (D)','DF'],                      selectedClass: 'bg-yellow-100 border-yellow-400 dark:bg-yellow-900/40 dark:border-yellow-400 text-yellow-700 dark:text-yellow-300' },
  { id: 'granules', emoji: '🔵', labelAr: 'محببات',         labelEn: 'Granules', values: ['WG','SG','GR','MG','محبب (G)','DG','EG'],                              selectedClass: 'bg-indigo-100 border-indigo-400 dark:bg-indigo-900/40 dark:border-indigo-400 text-indigo-700 dark:text-indigo-300' },
  { id: 'capsules', emoji: '💊', labelAr: 'كبسولات',        labelEn: 'Capsules', values: ['CS','كبسولات (CAP)','ZC'],                                         selectedClass: 'bg-pink-100 border-pink-400 dark:bg-pink-900/40 dark:border-pink-400 text-pink-700 dark:text-pink-300' },
  { id: 'bait_gel', emoji: '🧪', labelAr: 'طعم / جل',       labelEn: 'Bait/Gel', values: ['طعم (Bait)','جل (GEL)','CB'],                                     selectedClass: 'bg-teal-100 border-teal-400 dark:bg-teal-900/40 dark:border-teal-400 text-teal-700 dark:text-teal-300' },
  { id: 'tablets',  emoji: '⬡',  labelAr: 'أقراص',          labelEn: 'Tablets',  values: ['أقراص (TAB)'],                                                      selectedClass: 'bg-orange-100 border-orange-400 dark:bg-orange-900/40 dark:border-orange-400 text-orange-700 dark:text-orange-300' },
];

// ─── Thinking Accordion ──────────────────────────────────────────────────────
function ThinkingAccordion({ content, isRTL, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  if (!content) return null;
  return (
    <div className="mb-3 rounded-xl border border-purple-200 dark:border-purple-800 overflow-hidden">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between px-4 py-2.5 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs font-bold">
        <span className="flex items-center gap-2"><Brain size={14}/>{isRTL ? '🤔 تفكير الذكاء الاصطناعي' : '🤔 AI Thinking'}</span>
        <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`}/>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{height:0,opacity:0}} animate={{height:'auto',opacity:1}} exit={{height:0,opacity:0}} className="px-4 py-3 bg-purple-50/50 dark:bg-purple-900/10 text-xs text-purple-600 dark:text-purple-400 font-mono whitespace-pre-wrap max-h-48 overflow-auto">
            {content}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Markdown Renderer ───────────────────────────────────────────────────────
function AiMarkdown({ content }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        h1: ({node,...p}) => <h1 className="text-lg font-extrabold text-[#1B263B] dark:text-white mt-4 mb-2 border-b border-[#C47A2C]/30 pb-1" {...p}/>,
        h2: ({node,...p}) => <h2 className="text-base font-bold text-[#C47A2C] mt-3 mb-1.5" {...p}/>,
        h3: ({node,...p}) => <h3 className="text-sm font-bold text-[#6B8F71] mt-2 mb-1" {...p}/>,
        p:  ({node,...p}) => <p  className="text-sm leading-relaxed text-gray-700 dark:text-gray-300 mb-2" {...p}/>,
        ul: ({node,...p}) => <ul className="list-disc list-inside space-y-1 mb-2 text-sm text-gray-700 dark:text-gray-300" {...p}/>,
        ol: ({node,...p}) => <ol className="list-decimal list-inside space-y-1 mb-2 text-sm text-gray-700 dark:text-gray-300" {...p}/>,
        li: ({node,...p}) => <li className="leading-relaxed" {...p}/>,
        strong: ({node,...p}) => <strong className="font-bold text-[#1B263B] dark:text-white" {...p}/>,
        table: ({node,...p}) => <div className="overflow-x-auto my-3"><table className="min-w-full text-xs border-collapse rounded-lg overflow-hidden shadow-sm" {...p}/></div>,
        thead: ({node,...p}) => <thead className="bg-[#C47A2C]/10 dark:bg-[#C47A2C]/20" {...p}/>,
        th: ({node,...p}) => <th className="px-3 py-2 text-left font-bold text-[#1B263B] dark:text-white border border-[#C47A2C]/20 dark:border-slate-600" {...p}/>,
        td: ({node,...p}) => <td className="px-3 py-2 text-gray-700 dark:text-gray-300 border border-gray-100 dark:border-slate-700" {...p}/>,
        tr: ({node,...p}) => <tr className="even:bg-gray-50 dark:even:bg-slate-800/50 hover:bg-[#C47A2C]/5 transition-colors" {...p}/>,
        code: ({node,...p}) => <code className="bg-gray-100 dark:bg-slate-700 px-1.5 py-0.5 rounded text-xs font-mono text-[#C47A2C]" {...p}/>,
        blockquote: ({node,...p}) => <blockquote className="border-l-4 border-[#6B8F71] pl-3 italic text-gray-500 dark:text-gray-400 text-sm my-2" {...p}/>,
      }}
    >
      {content}
    </ReactMarkdown>
  );
}

// ─── Chat Bubble ─────────────────────────────────────────────────────────────
function ChatBubble({ role, content, thinking, isRTL, isStreaming, suggestedActions, onActionClick, onTranslate, onRegenerate, isLast }) {
  const isAI = role === 'assistant';
  const [isTranslating, setIsTranslating] = useState(false);

  const handleTranslateClick = async () => {
    if(onTranslate) {
      setIsTranslating(true);
      await onTranslate();
      setIsTranslating(false);
    }
  };

  return (
    <motion.div
      initial={{opacity:0, y:10}} animate={{opacity:1, y:0}}
      className={`flex gap-3 ${isAI ? 'justify-start' : 'justify-end'}`}
    >
      {isAI && (
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#C47A2C] to-[#6B8F71] flex items-center justify-center flex-shrink-0 shadow text-white text-base">
          🤖
        </div>
      )}
      <div className={`max-w-[85%] ${isAI ? 'order-1' : 'order-first'}`}>
        {isAI && thinking && <ThinkingAccordion content={thinking} isRTL={isRTL} defaultOpen={isStreaming}/>}
        <div className={`rounded-2xl px-4 py-3 text-sm shadow-sm ${
          isAI
            ? 'bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700 text-[#1B263B] dark:text-white rounded-tl-none'
            : 'bg-[#C47A2C] text-white rounded-tr-none'
        }`}>
          {isAI ? (
            isStreaming && !content ? (
              <div className="flex gap-1 items-center py-1">
                <span className="w-2 h-2 bg-[#C47A2C] rounded-full animate-bounce" style={{animationDelay:'0ms'}}/>
                <span className="w-2 h-2 bg-[#C47A2C] rounded-full animate-bounce" style={{animationDelay:'150ms'}}/>
                <span className="w-2 h-2 bg-[#C47A2C] rounded-full animate-bounce" style={{animationDelay:'300ms'}}/>
              </div>
            ) : <AiMarkdown content={content}/>
          ) : (
            <p className="leading-relaxed whitespace-pre-wrap">{content}</p>
          )}
        </div>
        
        {isAI && !isStreaming && (
          <div className={`mt-2 flex flex-wrap items-center gap-2 ${isRTL ? 'pr-2' : 'pl-2'}`}>
            {onTranslate && (
              <button onClick={handleTranslateClick} disabled={isTranslating} title={isRTL ? 'ترجمة' : 'Translate'}
                className="text-gray-400 hover:text-blue-500 transition-colors p-1 rounded-md hover:bg-blue-50 dark:hover:bg-slate-800 flex items-center gap-1 text-[11px] font-medium disabled:opacity-50"
              >
                {isTranslating ? <RefreshCw size={13} className="animate-spin" /> : <Languages size={13} />}
                {isRTL ? 'ترجمة' : 'Translate'}
              </button>
            )}
            {onRegenerate && isLast && (
              <button onClick={onRegenerate} title={isRTL ? 'إعادة التوليد' : 'Regenerate'}
                className="text-gray-400 hover:text-green-500 transition-colors p-1 rounded-md hover:bg-green-50 dark:hover:bg-slate-800 flex items-center gap-1 text-[11px] font-medium"
              >
                <RefreshCw size={13} />
                {isRTL ? 'إعادة التوليد' : 'Regenerate'}
              </button>
            )}
            {suggestedActions && suggestedActions.map((action, idx) => (
              <button
                key={idx}
                onClick={() => onActionClick(action)}
                className="text-xs font-bold px-3 py-1.5 bg-[#6B8F71]/10 text-[#6B8F71] border border-[#6B8F71]/30 rounded-full hover:bg-[#6B8F71] hover:text-white transition-colors text-right"
              >
                💡 {action}
              </button>
            ))}
          </div>
        )}
      </div>
      {!isAI && (
        <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-slate-600 flex items-center justify-center flex-shrink-0 shadow text-base">
          🧑‍🌾
        </div>
      )}
    </motion.div>
  );
}

// ─── Glowing Material Tag ─────────────────────────────────────────────────────
function GlowingMaterialTag({ label, onRemove, isNew }) {
  return (
    <motion.span
      initial={isNew ? { scale: 0, opacity: 0 } : { scale: 1, opacity: 1 }}
      animate={isNew
        ? { scale: [0, 1.15, 1], opacity: 1,
            boxShadow: [
              '0 0 0px rgba(196,122,44,0)',
              '0 0 18px rgba(196,122,44,0.9)',
              '0 0 0px rgba(196,122,44,0)',
              '0 0 18px rgba(196,122,44,0.9)',
              '0 0 0px rgba(196,122,44,0)',
              '0 0 18px rgba(196,122,44,0.9)',
              '0 0 6px rgba(196,122,44,0.3)',
            ],
          }
        : {}}
      transition={{ duration: 1.6, times: [0, 0.1, 0.3, 0.5, 0.7, 0.9, 1] }}
      exit={{ scale: 0, opacity: 0 }}
      className="inline-flex items-center gap-1 pl-2.5 pr-1 py-0.5 bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200 rounded-full text-[11px] font-semibold border border-[#C47A2C]/40 shadow-sm"
    >
      {label}
      <button type="button" onClick={onRemove} className="w-4 h-4 rounded-full bg-red-50 dark:bg-red-900/20 text-red-500 hover:bg-red-500 hover:text-white flex items-center justify-center transition-colors">
        <X size={10}/>
      </button>
    </motion.span>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────
const PesticideRecommenderView = ({ t, userId, isRTL }) => {
  const [globalContext, setGlobalContext] = useState({});
  const [materialsByType, setMaterialsByType] = useState({});
  const [materialsList, setMaterialsList] = useState([]);
  const [materialAliases, setMaterialAliases] = useState({});
  const [materialsLoading, setMaterialsLoading] = useState(true);

  // AI suggestion states (separate from consultation)
  const [aiSuggestLoading, setAiSuggestLoading] = useState(false);
  const [aiSuggestThinking, setAiSuggestThinking] = useState('');
  const [newlyAddedMaterials, setNewlyAddedMaterials] = useState(new Set());

  const [formData, setFormData] = useState({
    pesticide_types: ['حشرى'],   // ← now an ARRAY (multi-select)
    effective_materials: [],
    min_conc: 0,
    max_conc: 100,
    selectedGroups: [],
    user_case: '',
  });

  // Chat State
  const [chatHistory, setChatHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [streamingThinking, setStreamingThinking] = useState('');
  const [followUpText, setFollowUpText] = useState('');
  const chatEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const abortRef = useRef(null);

  // Follow the answer while the farmer is at the bottom; stop once they scroll up to read
  useEffect(() => {
    const box = chatContainerRef.current;
    if (autoScroll && box) box.scrollTop = box.scrollHeight;
  }, [chatHistory, streamingContent, streamingThinking, autoScroll]);

  // Never leave a stream running after the view unmounts
  useEffect(() => () => abortRef.current?.abort(), []);

  useEffect(() => {
    ApiService.get('/v1/pesticide-materials')
      .then(d => {
        const grouped = d.materials || {};
        setMaterialsByType(grouped);
        setMaterialsList([...new Set(Object.values(grouped).flat())].sort((a, b) => a.localeCompare(b)));
        setMaterialAliases(d.aliases || {});
      })
      .catch(console.error).finally(() => setMaterialsLoading(false));
  }, []);

  // Language switch: translate the conversation already on screen into the new language
  const previousRtlRef = useRef(isRTL);
  useEffect(() => {
    if (previousRtlRef.current === isRTL) return;
    previousRtlRef.current = isRTL;
    if (!chatHistory.length || isLoading) return;
    const targetLang = isRTL ? 'ar' : 'en';
    let cancelled = false;
    Promise.all(chatHistory.map(async (msg) => {
      if (msg.role !== 'assistant') return msg;
      try {
        return {
          ...msg,
          content: msg.content ? await TranslationService.translateText(msg.content, targetLang) : msg.content,
          thinking: msg.thinking ? await TranslationService.translateText(msg.thinking, targetLang) : msg.thinking,
        };
      } catch (e) {
        return msg;
      }
    })).then(translated => {
      // Skip if the conversation moved on while translating
      if (!cancelled) setChatHistory(prev => (prev.length === translated.length ? translated : prev));
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRTL]);

  // Dropdown: every single ingredient of the selected types once, A-Z (all types when none is
  // selected); the ones already picked are left out
  const dropdownMaterials = useMemo(() => {
    const types = formData.pesticide_types || [];
    const pool = types.length
      ? [...new Set(types.flatMap(t => materialsByType[t] || []))].sort((a, b) => a.localeCompare(b))
      : materialsList;
    const picked = new Set((formData.effective_materials || []).map(m => materialKey(m, materialAliases)));
    return pool.filter(m => !picked.has(materialKey(m, materialAliases)));
  }, [formData.pesticide_types, formData.effective_materials, materialsByType, materialsList, materialAliases]);

  // ── Multi-select pesticide types ─────────────────────────────────────────
  const togglePesticideType = (id) => {
    setFormData(prev => {
      const types = prev.pesticide_types;
      if (types.includes(id)) {
        // Don't allow deselecting the last one
        return types.length === 1 ? prev : { ...prev, pesticide_types: types.filter(t => t !== id) };
      }
      return { ...prev, pesticide_types: [...types, id] };
    });
  };

  const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const toggleGroup = (id) => setFormData(prev => ({
    ...prev, selectedGroups: prev.selectedGroups.includes(id)
      ? prev.selectedGroups.filter(g => g !== id)
      : [...prev.selectedGroups, id]
  }));

  const handleAddMaterial = (e) => {
    const val = e.target.value;
    if (val && !(formData.effective_materials || []).includes(val))
      setFormData(prev => ({ ...prev, effective_materials: [...(prev.effective_materials || []), val] }));
    e.target.value = '';
  };

  const removeMaterial = (m) => {
    setFormData(prev => ({ ...prev, effective_materials: (prev.effective_materials || []).filter(x => x !== m) }));
    setNewlyAddedMaterials(prev => { const n = new Set(prev); n.delete(m); return n; });
  };

  // ── AI suggests type + ALL suitable materials ────────────────────────────
  // Does NOT start consultation — only fills the form
  const handleAiSuggestAll = async () => {
    if (!formData.user_case.trim()) return;
    setAiSuggestLoading(true);
    setAiSuggestThinking('');

    try {
      // The backend builds the extraction prompt from the DB ingredient index (grouped by type)
      const res = await fetch(`${API_URL}/v1/pesticide-suggest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_case: formData.user_case,
          context: globalContext,
          lang: isRTL ? 'ar' : 'en'
        })
      });
      if (!res.ok) throw new Error(`Server error (${res.status})`);

      let text = '';
      let thinkingText = '';
      await readSSE(res, (evt) => {
        if (evt.reasoning) { thinkingText += evt.reasoning; setAiSuggestThinking(thinkingText); }
        else if (evt.content) { text += evt.content; }
      });

      // ── Clear thinking display after AI finishes ─────────────────────────
      setTimeout(() => setAiSuggestThinking(''), 800);

      // The answer ends with the fixed lines; take the last occurrence of each
      const lastMatch = (re) => [...text.matchAll(re)].pop();

      // ── Parse TYPES ────────────────────────────────────────────────────
      const typesMatch = lastMatch(/TYPES:\s*\[([^\]]+)\]/gi);
      if (typesMatch) {
        const rawTypes = typesMatch[1].split(',').map(s => typeKey(s.replace(/['"]/g, '')));
        const validTypes = PESTICIDE_TYPES.map(pt => pt.id);
        const selected = [...new Set(rawTypes.filter(t => validTypes.includes(t)))];
        if (selected.length > 0) setFormData(prev => ({ ...prev, pesticide_types: selected }));
      }

      // ── Parse MATERIALS and add with glow ─────────────────────────────
      const matsMatch = lastMatch(/MATERIALS:\s*\[([^\]]+)\]/gi);
      if (matsMatch) {
        const suggested = parseSuggestedMaterials(matsMatch[1], materialsList, materialAliases);
        if (suggested.length > 0) {
          setFormData(prev => ({
            ...prev,
            effective_materials: [...new Set([...(prev.effective_materials || []), ...suggested])]
          }));
          setNewlyAddedMaterials(new Set(suggested));
          setTimeout(() => setNewlyAddedMaterials(new Set()), 2500);
        }
      }
    } catch (e) { console.error(e); }
    setAiSuggestLoading(false);
  };

  // ── Shared SSE runner for the consultation and follow-up turns ───────────
  const runChatStream = useCallback(async (url, payload, extraFields = {}) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setIsLoading(true);
    setStreamingContent('');
    setStreamingThinking('');
    setAutoScroll(true);

    let fullContent = '';
    let fullThinking = '';
    try {
      const res = await fetch(url, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload), signal: controller.signal
      });
      if (!res.ok) throw new Error(`Server error (${res.status})`);

      /* Buffered SSE read: <think> spans arrive on the reasoning channel */
      await readSSE(res, (evt) => {
        if (evt.error) { fullContent += `

❌ ${evt.error}`; setStreamingContent(fullContent); }
        else if (evt.reasoning) { fullThinking += evt.reasoning; setStreamingThinking(fullThinking); }
        else if (evt.content) { fullContent += evt.content; setStreamingContent(fullContent); }
      });

      setChatHistory(prev => [...prev, { role: 'assistant', content: fullContent, thinking: fullThinking, ...extraFields }]);
    } catch (e) {
      // AbortError = "New Chat" or leaving the page: the partial answer is dropped
      if (e.name !== 'AbortError') {
        setChatHistory(prev => [...prev, { role: 'assistant', content: `❌ ${e.message}`, thinking: '' }]);
      }
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null;
        setStreamingContent('');
        setStreamingThinking('');
        setIsLoading(false);
      }
    }
  }, []);

  // ── Stream from backend (called ONLY when button is clicked) ─────────────
  const streamFromBackend = useCallback((conversationHistory) => {
    const formulations = formData.selectedGroups.flatMap(id => FORMULATION_GROUPS.find(g => g.id === id)?.values || []);
    const payload = {
      pesticide_types: formData.pesticide_types,
      pesticide_type: formData.pesticide_types[0],
      effective_materials: formData.effective_materials,
      min_conc: parseFloat(formData.min_conc) || 0,
      max_conc: parseFloat(formData.max_conc) || 100,
      target_formulations: formulations,
      user_case: formData.user_case,
      lang: isRTL ? 'ar' : 'en',
      context: globalContext,
      conversation_history: conversationHistory,
    };
    return runChatStream(`${API_URL}/v1/pesticide-recommend`, payload, {
      suggestedActions: [isRTL ? 'هل يمكنك اقتراح خطة عمل (برنامج مكافحة متكامل) باستخدام هذه المبيدات؟' : 'Can you suggest an action plan using these pesticides?']
    });
  }, [formData, globalContext, isRTL, runChatStream]);

  // Follow-ups: the IPM follow-up system prompt lives on the server (/v1/pesticide-followup)
  const streamGenericFollowUp = useCallback((conversationHistory) =>
    runChatStream(`${API_URL}/v1/pesticide-followup`, {
      conversation_history: conversationHistory,
      lang: isRTL ? 'ar' : 'en',
    }), [isRTL, runChatStream]);

  const handleFirstAsk = () => {
    if (!formData.user_case.trim()) return;
    setChatHistory([{ role: 'user', content: formData.user_case }]);
    streamFromBackend([]);
  };

  const handleFollowUp = (textOverride = null) => {
    const textToUse = typeof textOverride === 'string' ? textOverride : followUpText;
    if (!textToUse.trim() || isLoading) return;
    const userMsg = { role: 'user', content: textToUse };
    const newHistory = [...chatHistory, userMsg];
    setChatHistory(newHistory);
    if (typeof textOverride !== 'string') setFollowUpText('');
    
    const apiHistory = newHistory.map(m => ({ role: m.role, content: m.content }));
    streamGenericFollowUp(apiHistory);
  };

  const resetChat = () => { abortRef.current?.abort(); setChatHistory([]); setStreamingContent(''); setStreamingThinking(''); };

  // ── Per-message actions ───────────────────────────────────────────────────
  // Translate flips a reply between Arabic and English, whichever it is not written in
  const translateMessage = async (index) => {
    const msg = chatHistory[index];
    if (!msg || !msg.content) return;
    const arabic = (msg.content.match(/[؀-ۿ]/g) || []).length;
    const latin = (msg.content.match(/[A-Za-z]/g) || []).length;
    const translated = await TranslationService.translateText(msg.content, arabic > latin ? 'en' : 'ar');
    setChatHistory(prev => prev.map((m, i) => (i === index && m === msg ? { ...m, content: translated } : m)));
  };

  // Regenerate re-asks the last question: the opening consultation or the last follow-up
  const regenerateLast = () => {
    if (isLoading) return;
    const lastUser = chatHistory.map(m => m.role).lastIndexOf('user');
    if (lastUser === -1) return;
    const kept = chatHistory.slice(0, lastUser + 1);
    setChatHistory(kept);
    if (lastUser === 0) streamFromBackend([]);
    else streamGenericFollowUp(kept.map(m => ({ role: m.role, content: m.content })));
  };

  const inputStyle = "w-full p-2.5 rounded-lg border border-gray-200 dark:border-slate-600 bg-gray-50/50 dark:bg-slate-700/50 text-[#1B263B] dark:text-white focus:ring-2 focus:ring-[#C47A2C] focus:border-transparent transition-all outline-none text-sm";
  const lblClass = "block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1.5";
  const hasChatStarted = chatHistory.length > 0 || isLoading;

  return (
    <div className="h-full flex flex-col max-w-7xl mx-auto font-sans text-[#1B263B] dark:text-white pb-10 space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-3xl font-bold flex items-center gap-3 text-[#1B263B] dark:text-white">
            <span className="text-3xl">🧪</span>
            {isRTL ? 'استشاري المبيدات الذكي' : 'Smart Pesticide Advisor'}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {isRTL ? 'قاعدة بيانات +4700 مبيد زراعي مسجل • ذكاء اصطناعي متخصص' : '4700+ registered pesticides • Specialized AI consultant'}
          </p>
        </div>
        {hasChatStarted && (
          <button onClick={resetChat} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg border border-gray-200 dark:border-slate-600 text-gray-500 hover:text-red-500 hover:border-red-300 transition-colors">
            <RotateCcw size={12}/> {isRTL ? 'محادثة جديدة' : 'New Chat'}
          </button>
        )}
      </div>

      {/* Context Bar */}
      <GlobalContextBar t={t} isRTL={isRTL} onContextChange={setGlobalContext}/>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* ── LEFT: Form ──────────────────────────────────────────────────── */}
        <div className="lg:col-span-1 space-y-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-[#1B263B]/5 dark:border-slate-700 shadow-sm space-y-5">
            <h3 className="font-bold text-base text-[#1B263B] dark:text-white flex items-center gap-2">
              <Sparkles size={18} className="text-[#6B8F71]"/>
              {isRTL ? 'معايير البحث' : 'Search Criteria'}
            </h3>

            {/* ── Pesticide Type Cards (multi-select) ─── */}
            <div>
              <label className={lblClass}>
                {isRTL ? 'نوع المبيد (يمكن اختيار أكثر من نوع)' : 'Pesticide Type (multi-select)'}
              </label>
              <div className="grid grid-cols-3 gap-2">
                {PESTICIDE_TYPES.map(type => {
                  const sel = formData.pesticide_types.includes(type.id);
                  return (
                    <motion.button
                      key={type.id} type="button"
                      whileHover={{ scale: 1.04, y: -2 }}
                      whileTap={{ scale: 0.96 }}
                      onClick={() => togglePesticideType(type.id)}
                      style={sel ? { boxShadow: `0 0 20px 3px ${type.glowColor}` } : {}}
                      className={`relative flex flex-col items-center justify-center py-3 px-1.5 rounded-xl border-2 transition-all duration-200 cursor-pointer text-center overflow-hidden ${
                        sel
                          ? `bg-gradient-to-br ${type.selectedBg} border-transparent text-white shadow-lg ring-2 ${type.selectedRing} ring-offset-1`
                          : `${type.bg} ${type.border} text-gray-700 dark:text-gray-200`
                      }`}
                    >
                      {/* Checkmark badge when selected */}
                      {sel && (
                        <motion.div
                          initial={{ scale: 0 }} animate={{ scale: 1 }}
                          className="absolute top-1 right-1 w-4 h-4 bg-white/30 rounded-full flex items-center justify-center text-[9px] font-black"
                        >✓</motion.div>
                      )}
                      <span className={sel ? 'text-white' : type.colorClass}>
                        {type.icon}
                      </span>
                      <span className="text-[11px] font-bold leading-tight mt-1">
                        {isRTL ? type.labelAr : type.labelEn}
                      </span>
                    </motion.button>
                  );
                })}
              </div>
              {/* Show selected types as pills */}
              <div className="flex flex-wrap gap-1 mt-2">
                {formData.pesticide_types.map(typeId => {
                  const t = PESTICIDE_TYPES.find(x => x.id === typeId);
                  if (!t) return null;
                  return (
                    <span key={typeId} className={`text-[10px] px-2 py-0.5 rounded-full font-bold text-white bg-gradient-to-r ${t.selectedBg}`}>
                      {isRTL ? t.labelAr : t.labelEn}
                    </span>
                  );
                })}
              </div>
            </div>

            {/* ── Problem Description ─── */}
            <div>
              <label className={lblClass}>{isRTL ? 'وصف المشكلة' : 'Problem Description'}</label>
              <textarea name="user_case" value={formData.user_case} onChange={handleChange}
                rows={3}
                placeholder={isRTL ? 'مثال: عندي إصابة شديدة بالمن في محصول القمح...' : 'e.g. Severe aphid attack on wheat...'}
                className={inputStyle + " resize-none"}
              />
            </div>

            {/* ── Active Material + AI suggest ─── */}
            <div className="bg-gray-50/60 dark:bg-slate-900/30 p-3 rounded-xl border border-gray-100 dark:border-slate-700/50 space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-bold text-gray-700 dark:text-gray-300">
                  {isRTL ? 'المادة الفعالة' : 'Active Ingredient'}
                </label>

                <div className="flex items-center gap-2">
                  {/* Reset Selection Button */}
                  {(formData.effective_materials?.length > 0 || formData.pesticide_types?.length > 0) && (
                    <motion.button type="button" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                      onClick={() => {
                        setFormData(prev => ({ ...prev, effective_materials: [], pesticide_types: [] }));
                        setNewlyAddedMaterials(new Set());
                        setAiSuggestThinking('');
                      }}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-bold rounded-lg bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 hover:bg-red-200 dark:hover:bg-red-800 transition-colors"
                      title={isRTL ? 'إعادة ضبط' : 'Reset'}
                    >
                      <RotateCcw size={11} />
                      {isRTL ? 'إعادة ضبط' : 'Reset'}
                    </motion.button>
                  )}

                  {/* AI Suggest Button */}
                  <motion.button type="button" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                    onClick={handleAiSuggestAll}
                    disabled={aiSuggestLoading || !formData.user_case.trim() || materialsLoading}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-bold rounded-lg bg-[#C47A2C]/10 text-[#C47A2C] border border-[#C47A2C]/25 hover:bg-[#C47A2C] hover:text-white disabled:opacity-50 transition-colors"
                  >
                    {aiSuggestLoading ? <RefreshCw size={11} className="animate-spin"/> : <Wand2 size={11}/>}
                    {aiSuggestLoading ? (isRTL ? 'بفكر...' : 'Thinking...') : (isRTL ? 'AI اختيار النوع + المادة' : 'AI Pick Type + Material')}
                  </motion.button>
                </div>
              </div>

              {/* AI Thinking indicator (shows while AI suggests, then disappears) */}
              <AnimatePresence>
                {aiSuggestThinking && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="flex items-start gap-2 p-2 rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800"
                  >
                    <Brain size={13} className="text-purple-500 flex-shrink-0 mt-0.5 animate-pulse"/>
                    <p className="text-[10px] text-purple-600 dark:text-purple-400 font-mono line-clamp-2">
                      {aiSuggestThinking.slice(-200)}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Select dropdown */}
              <select onChange={handleAddMaterial} defaultValue="" disabled={materialsLoading} className={inputStyle}>
                <option value="" disabled>
                  {materialsLoading ? '...' : (isRTL ? '🔍 اختر مادة فعالة...' : '🔍 Select active ingredient...')}
                </option>
                {dropdownMaterials.map(m => <option key={m} value={m}>{m}</option>)}
              </select>

              {/* Material Tags with glow animation */}
              <AnimatePresence>
                {(formData.effective_materials || []).length > 0 && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="flex flex-wrap gap-1.5 pt-1">
                    {(formData.effective_materials || []).map(m => (
                      <GlowingMaterialTag
                        key={m}
                        label={m}
                        onRemove={() => removeMaterial(m)}
                        isNew={newlyAddedMaterials.has(m)}
                      />
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* ── Formulation Groups ─── */}
            <div>
              <label className={lblClass}>{isRTL ? 'شكل المبيد (اختياري)' : 'Formulation (optional)'}</label>
              <div className="flex flex-wrap gap-1.5">
                {FORMULATION_GROUPS.map(g => {
                  const sel = formData.selectedGroups.includes(g.id);
                  return (
                    <button key={g.id} type="button" onClick={() => toggleGroup(g.id)}
                      className={`px-2.5 py-1 text-[11px] rounded-lg border transition-all flex items-center gap-1 ${sel ? g.selectedClass + ' font-bold' : 'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50'}`}
                    >
                      <span>{g.emoji}</span> {isRTL ? g.labelAr : g.labelEn}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ── START CONSULTATION Button ─── */}
            <motion.button
              whileHover={{ y: -1 }} whileTap={{ scale: 0.98 }}
              onClick={handleFirstAsk}
              disabled={isLoading || !formData.user_case.trim()}
              className="w-full bg-[#C47A2C] hover:bg-[#A36322] text-white font-bold py-3.5 rounded-xl shadow transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isLoading
                ? <><RefreshCw size={20} className="animate-spin"/> {isRTL ? 'جاري التحليل...' : 'Analyzing...'}</>
                : <><span className="text-xl">🧪</span> {isRTL ? 'ابدأ الاستشارة' : 'Start Consultation'}</>
              }
            </motion.button>
          </div>
        </div>

        {/* ── RIGHT: Chat Area ─────────────────────────────────────────────── */}
        <div className="lg:col-span-2 flex flex-col">
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-[#1B263B]/5 dark:border-slate-700 shadow-sm flex flex-col overflow-hidden" style={{ minHeight: '500px', maxHeight: '75vh' }}>

            {/* Chat Header */}
            <div className="flex items-center gap-3 px-5 py-3.5 border-b border-gray-100 dark:border-slate-700 bg-gray-50/50 dark:bg-slate-900/30">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#C47A2C] to-[#6B8F71] flex items-center justify-center shadow text-base">🤖</div>
              <div>
                <p className="text-sm font-bold text-[#1B263B] dark:text-white">AgriBot</p>
                <p className="text-[10px] text-gray-400">{isRTL ? 'مستشار المبيدات الزراعي' : 'Agricultural Pesticide Advisor'}</p>
              </div>

              {hasChatStarted && (
                <button onClick={resetChat} title={isRTL ? 'إعادة ضبط المحادثة' : 'Reset Chat'} className="ml-auto flex items-center gap-1 p-1.5 text-xs rounded border border-red-200 dark:border-red-900/50 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                  <RotateCcw size={14}/>
                </button>
              )}

              <div className={`${hasChatStarted ? 'ml-3' : 'ml-auto'} w-2 h-2 rounded-full ${isLoading ? 'bg-[#C47A2C] animate-pulse' : hasChatStarted ? 'bg-green-400' : 'bg-gray-300 dark:bg-gray-600'}`}/>
            </div>

            {/* Messages */}
            <div
              ref={chatContainerRef}
              onScroll={(e) => {
                const { scrollTop, scrollHeight, clientHeight } = e.target;
                setAutoScroll(scrollHeight - scrollTop - clientHeight < 50);
              }}
              className="flex-1 overflow-y-auto p-4 space-y-4"
            >
              {!hasChatStarted && (
                <div className="flex flex-col items-center justify-center h-full text-center py-16 opacity-50">
                  <div className="w-20 h-20 rounded-full bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-slate-800 flex items-center justify-center mb-4 shadow-inner text-4xl">🧪</div>
                  <h4 className="text-base font-bold text-[#1B263B] dark:text-white mb-1">{isRTL ? 'ابدأ الاستشارة' : 'Start Your Consultation'}</h4>
                  <p className="text-xs text-gray-400 max-w-xs leading-relaxed">
                    {isRTL
                      ? 'اكتب مشكلتك، استخدم AI لاختيار المواد الفعالة، ثم اضغط "ابدأ الاستشارة".'
                      : 'Describe your problem, use AI to pick ingredients, then press Start.'}
                  </p>
                </div>
              )}

              {chatHistory.map((msg, i) => (
                <ChatBubble 
                  key={i} 
                  role={msg.role} 
                  content={msg.content} 
                  thinking={msg.thinking} 
                  isRTL={isRTL} 
                  isStreaming={false}
                  suggestedActions={msg.suggestedActions}
                  onActionClick={(action) => handleFollowUp(action)}
                  onTranslate={msg.role === 'assistant' ? () => translateMessage(i) : undefined}
                  onRegenerate={regenerateLast}
                  isLast={i === chatHistory.length - 1 && !isLoading}
                />
              ))}

              {isLoading && (
                <ChatBubble role="assistant" content={streamingContent} thinking={streamingThinking} isRTL={isRTL} isStreaming={true}/>
              )}

              <div ref={chatEndRef}/>
            </div>

            {/* Follow-up Input */}
            <AnimatePresence>
              {hasChatStarted && !isLoading && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  className="border-t border-gray-100 dark:border-slate-700 p-3 bg-white dark:bg-slate-800"
                >
                  <div className="flex gap-2 items-end">
                    <textarea
                      value={followUpText}
                      onChange={e => setFollowUpText(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleFollowUp(); }}}
                      placeholder={isRTL ? '💬 ردّ على الاستشاري... (Enter للإرسال)' : '💬 Reply to the advisor... (Enter to send)'}
                      rows={2}
                      className="flex-1 resize-none text-sm p-3 rounded-xl border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-700/50 text-[#1B263B] dark:text-white focus:ring-2 focus:ring-[#C47A2C] focus:border-transparent outline-none transition-all"
                    />
                    <motion.button
                      whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                      onClick={handleFollowUp}
                      disabled={!followUpText.trim()}
                      className="w-11 h-11 rounded-xl bg-[#C47A2C] hover:bg-[#A36322] text-white flex items-center justify-center shadow transition-all disabled:opacity-40"
                    >
                      <Send size={18} className={isRTL ? 'rotate-180' : ''}/>
                    </motion.button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

      </div>
    </div>
  );
};

export default PesticideRecommenderView;
